import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { automationsAPI } from '@/shared/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { formatPercentage } from '@/shared/lib/utils';
import { Zap } from 'lucide-react';
import type { AutomationWithStats } from '@/shared/types';
import { Button } from '@travellegacy/nexops-design-system';
import { AutomationTypeBadge } from '../../automation/components/AutomationTypeBadge';

export function AutomationsList() {
  const { data, isLoading } = useQuery({
    queryKey: ['automations-aggregated', 'active'],
    queryFn: async () => {
      const response = await automationsAPI.listAggregated({
        isActive: true,
      });
      return response.data.data as AutomationWithStats[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutos - alinhado com cache
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Zap className="mr-2 h-5 w-5" />
            Automações Ativas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-20 bg-muted rounded" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center">
            <Zap className="mr-2 h-5 w-5" />
            Automações Ativas
          </span>
          <Link
            to="/automations"
            className="text-sm font-normal text-primary hover:text-primary/80"
          >
            Ver todas →
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!data || data.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">Nenhuma automação ativa</div>
        ) : (
          <div className="space-y-3">
            {data.slice(0, 5).map((automation) => (
              <div key={automation.id} className="border rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-card-foreground">{automation.name}</h4>
                    {automation.description && (
                      <p className="text-sm text-muted-foreground mt-1">{automation.description}</p>
                    )}
                  </div>
                  <Link to={`/automations/${automation.id}`}>
                    <Button variant="ghost" size="sm">
                      Ver detalhes
                    </Button>
                  </Link>
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between mt-3 pt-3 border-t">
                  <div className="flex space-x-4 text-sm">
                    <div>
                      <AutomationTypeBadge type={automation.type} size="sm" />
                    </div>
                    <div>
                      <span className="text-muted-foreground">Total: </span>
                      <span className="font-medium text-card-foreground">
                        {automation.stats.totalExecutions}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Taxa: </span>
                      <span
                        className={
                          automation.stats.successRate >= 90
                            ? 'font-medium text-green-600 dark:text-green-400'
                            : automation.stats.successRate >= 70
                              ? 'font-medium text-yellow-600 dark:text-yellow-400'
                              : 'font-medium text-red-600 dark:text-red-400'
                        }
                      >
                        {formatPercentage(automation.stats.successRate)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
